/**
 * Narrows a URL-supplied channel selection to the channels a surface may act
 * on. The `channels[]` param is user-editable state (saved views, bookmarks,
 * hand-typed URLs), so every all-channels view — Publish, Community,
 * Insights — must apply its selection as this intersection, not the raw
 * param: ids unknown to the organization and ids in the surface's
 * `disabledChannelIds` set are dropped.
 *
 * Each surface supplies its own product rule through `disabledChannelIds`,
 * so the three dropdowns behave identically while gating on their own
 * action. For the per-surface rules, @see `getDisabledChannelIds` (Publish),
 * `useAllChannelsData` in Community's `AllChannelsView`, and
 * `disabledChannelIds` in `AllChannelsInsightsPage`.
 *
 * While the channel list is still loading an empty `channels` is
 * indistinguishable from "no access", so the raw selection passes through
 * rather than being mistaken for all-inaccessible.
 */
export function selectEnabledChannelIds(
  selectedChannelIds: string[],
  channels: readonly { id: string }[],
  disabledChannelIds: ReadonlySet<string> | undefined,
): string[] {
  if (channels.length === 0) return selectedChannelIds
  return selectedChannelIds.filter(
    (id) =>
      !disabledChannelIds?.has(id) &&
      channels.some((channel) => channel.id === id),
  )
}

/**
 * True when an explicit selection exists but none of it is enabled on the
 * surface — the page must show its access state instead of silently widening
 * an empty effective selection back to "all channels". False while the
 * channel list is still loading.
 */
export function hasOnlyInaccessibleChannels(
  selectedChannelIds: string[],
  effectiveChannelIds: string[],
  channels: readonly { id: string }[],
): boolean {
  return (
    channels.length > 0 &&
    selectedChannelIds.length > 0 &&
    effectiveChannelIds.length === 0
  )
}
